import React, { createContext, useContext, useState, ReactNode } from 'react'

interface PillarInputs {
  monthlyVolume: number | ''
  targetAutomationPercentage: number
  manualInterventionPercentage: number | ''
  avgTimePerTask: number | ''
  useBenchmark: boolean
}

interface GeneralAssumptions {
  totalFTEs: number
  annualCostPerFTE: number
  selectedAreas: string[]
  useGlobalBenchmark: boolean
}

interface SimpleROIData {
  generalAssumptions?: GeneralAssumptions
  pillarInputs?: Record<string, PillarInputs>
  calculatedResults?: {
    totalInvestment: number
    totalSavings: number
    roiPercentage: number
    paybackMonths: number
    pillarBreakdown: Record<string, {
      savings: number
      volume: number
      automationPercentage: number
    }>
  }
  // Core fields
  revenueBand?: string
  painPoints?: string[]
  selectedAreas?: string[]
  useGlobalBenchmark?: boolean
  totalFTEs?: number
  annualCostPerFTE?: number
  closeDays?: number
  organizationSize?: string
  // Legacy fields for backward compatibility
  legalEntities?: number | ''
  closeTeamSize?: number | ''
  currentCloseDays?: number | ''
  targetCloseDays?: number | ''
}

interface SimpleROIContext {
  data: SimpleROIData
  updateData: (updates: Partial<SimpleROIData>) => void
  updateFormData: (formData: { generalAssumptions: GeneralAssumptions, pillarInputs: Record<string, PillarInputs> }) => void
  clearData: () => void
}

const defaultData: SimpleROIData = {
  generalAssumptions: undefined,
  pillarInputs: undefined,
  calculatedResults: undefined,
  // Legacy defaults
  revenueBand: '',
  legalEntities: '',
  closeTeamSize: '',
  currentCloseDays: '',
  targetCloseDays: '',
  painPoints: []
}

const SimpleROIContext = createContext<SimpleROIContext | undefined>(undefined)

export function SimpleROIProvider({ children }: { children: ReactNode }) {
  const [data, setData] = useState<SimpleROIData>(() => {
    // Initialize with localStorage data if available
    try {
      const storedData = localStorage.getItem('simpleROIData')
      if (storedData) {
        const parsed = JSON.parse(storedData)
        return { ...defaultData, ...parsed }
      }
    } catch (error) {
      console.error('Error loading stored ROI data:', error)
    }
    return defaultData
  })

  const updateData = (updates: Partial<SimpleROIData>) => {
    setData(prev => ({ ...prev, ...updates }))
  }

  const updateFormData = (formData: { generalAssumptions: GeneralAssumptions, pillarInputs: Record<string, PillarInputs> }) => {
    setData(prev => ({
      ...prev,
      generalAssumptions: formData.generalAssumptions,
      pillarInputs: formData.pillarInputs
    }))
  }

  const clearData = () => {
    setData(defaultData)
  }

  return (
    <SimpleROIContext.Provider value={{ data, updateData, updateFormData, clearData }}>
      {children}
    </SimpleROIContext.Provider>
  )
}

export function useSimpleROI() {
  const context = useContext(SimpleROIContext)
  if (context === undefined) {
    throw new Error('useSimpleROI must be used within a SimpleROIProvider')
  }
  return context
}